import React, { useEffect, useState } from "react"
import { useDarkMode } from '../utils/DarkModeProvider'
import stylesTableDarkMode from '../styles/TableDarkMode'
import stylesTableLightMode from '../styles/TableLightMode'
import { useGlobal } from '../utils/GlobalContext'
import Swal from 'sweetalert2'
import { useLocation } from 'react-router-dom'

const Modal = ({ elem, isOpen, closeModal, esEdicion = false, index = null, idActualizar = null, notas = null, evaluacion = null, soloNotas = false }) => {

    const location = useLocation()

    const { darkMode } = useDarkMode()

    const { globalStateAgregarInstancia, globalState, globalStateActualizarInstancia } = useGlobal()

    const dynamicStyles = darkMode ? stylesTableDarkMode : stylesTableLightMode

    const convertToInputDate = (value) => {
        if (!value) return ""
        if (value.includes("-")) return value
        if (value.includes("/")) {
            const [day, month, year] = value.split("/")
            return `${year}-${month}-${day}`
        }
        return value
    }

    const convertToDisplayDate = (value) => {
        if (!value) return ""
        if (value.includes("/")) return value
        if (value.includes("-")) {
            const [year, month, day] = value.split("-")
            return `${day}/${month}/${year}`
        }
        return value
    }

    const normalizarFechaInput = (value) => {
        if (!value || typeof value !== 'string') return ''
        if (value.includes('-')) return value
        if (value.includes('/')) {
            const [day, month, year] = value.split('/')
            if (!day || !month || !year) return ''
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        }
        return ''
    }

    const esFechaRealValida = (value) => {
        const fechaNormalizada = normalizarFechaInput(value)
        if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaNormalizada)) return false

        const [anioStr, mesStr, diaStr] = fechaNormalizada.split('-')
        const anio = Number(anioStr)
        const mes = Number(mesStr)
        const dia = Number(diaStr)

        if (!anio || !mes || !dia) return false

        const fecha = new Date(Date.UTC(anio, mes - 1, dia))
        return (
            fecha.getUTCFullYear() === anio &&
            fecha.getUTCMonth() === (mes - 1) &&
            fecha.getUTCDate() === dia
        )
    }

    const evaluacionActual = evaluacion || location.state?.evaluacion
    const fechaMinimaEvaluacion = normalizarFechaInput(evaluacionActual?.fecha_inicio)
    const fechaMaximaEvaluacion = normalizarFechaInput(evaluacionActual?.fecha_fin)

    const estaFechaDentroDelPeriodo = (fecha) => {
        if (!fecha) return true
        if (!fechaMinimaEvaluacion || !fechaMaximaEvaluacion) return true
        return fecha >= fechaMinimaEvaluacion && fecha <= fechaMaximaEvaluacion
    }

    const atributosElem = Array.isArray(elem?.atributos) ? elem.atributos : []

    // const [inputValues, setInputValues] = useState(!esEdicion && Array(atributosElem.length).fill(""))

    const [inputValues, setInputValues] = useState(() =>
        esEdicion ?
            atributosElem.map(attr => {
                const foundValue = elem.instancias[index].valores.find(val => val.nombre === attr.nombre);
                const valor = foundValue ? foundValue.valor : "";
                return attr.tipo === "date" ? convertToInputDate(valor) : valor;
            })
            :
            Array(atributosElem.length).fill("")
    );

    const [inputErrors, setInputErrors] = useState(new Array(atributosElem.length).fill(false))
    const [listadoSearchValues, setListadoSearchValues] = useState(
        Array(atributosElem.length).fill("")
    )
    const [listadoAbiertoIndex, setListadoAbiertoIndex] = useState(null)
    const [listadoHighlightedIndices, setListadoHighlightedIndices] = useState(
        Array(atributosElem.length).fill(-1)
    )

    useEffect(() => {
        if (isOpen) {
            const valoresIniciales = esEdicion
                ? atributosElem.map(attr => {
                    const foundValue = elem.instancias[index].valores.find(val => val.nombre === attr.nombre);
                    let valor = foundValue ? foundValue.valor : "";

                    if (attr.tipo === "date") {
                        valor = convertToInputDate(valor)
                    }

                    return valor;
                })
                : Array(atributosElem.length).fill("")

            if (esEdicion) {
                setInputValues(valoresIniciales);
            } else {
                setInputValues(Array(atributosElem.length).fill(""));
            }
            setListadoSearchValues(
                atributosElem.map((attr, attrIndex) => attr.tipo === 'listado' ? (valoresIniciales[attrIndex] || '') : '')
            )
            setListadoAbiertoIndex(null)
            setListadoHighlightedIndices(Array(atributosElem.length).fill(-1))
            setInputErrors(new Array(atributosElem.length).fill(false));
        }
    }, [isOpen, esEdicion, index, elem, atributosElem]);

    if (!isOpen) return null

    const getInputType = (tipo) => {
        switch (tipo) {
            case "string":
                return "text"
            case "numero_entero":
                return "text"
            case "numero_decimal":
                return "text"
            case "numero_con_guion":
                return "text"
            case "date":
                return "date"
            case "texto_largo":
                return "textarea"
            case "listado":
                return "select"
            default:
                return "text"
        }
    }

    const sanitizeNumericInput = (tipo, rawValue) => {
        if (typeof rawValue !== 'string') return ''
        const valorNormalizado = rawValue.replace(',', '.')

        if (tipo === 'numero_entero') {
            return valorNormalizado.replace(/\D/g, '')
        }

        if (tipo === 'numero_decimal') {
            const sinInvalidos = valorNormalizado.replace(/[^\d.]/g, '')
            const partes = sinInvalidos.split('.')
            if (partes.length <= 1) return sinInvalidos
            return `${partes[0]}.${partes.slice(1).join('')}`
        }

        if (tipo === 'numero_con_guion') {
            return rawValue.replace(/[^0-9-]/g, '')
        }

        return rawValue
    }

    const isValorNumericoValido = (tipo, value) => {
        const valor = typeof value === 'string' ? value.trim() : ''
        if (tipo === 'numero_entero') {
            return /^\d+$/.test(valor)
        }
        if (tipo === 'numero_decimal') {
            return /^\d+(\.\d+)?$/.test(valor)
        }
        if (tipo === 'numero_con_guion') {
            return /^[0-9-]+$/.test(valor)
        }
        return valor !== ''
    }

    const getListadoSeleccionado = (fieldIndex, opciones) => {
        const opcionesNormalizadas = opciones || []
        const valorActual = (inputValues[fieldIndex] || '').trim()
        if (valorActual) return valorActual

        const textoBusqueda = (listadoSearchValues[fieldIndex] || '').trim().toLowerCase()
        if (!textoBusqueda) return ''

        const match = opcionesNormalizadas.find((opcion) => opcion.trim().toLowerCase() === textoBusqueda)
        return match || ''
    }

    const esAtributoFechaConNombre = (atributo, nombreEsperado) => (
        atributo.tipo === 'date' && atributo.nombre.trim().toLowerCase() === nombreEsperado
    )

    const validarValores = (valoresNormalizados) => {
        const erroresCampos = atributosElem.map((atributo, index) => {
            const valor = valoresNormalizados[index]
            if (atributo.tipo === 'numero_entero' || atributo.tipo === 'numero_decimal' || atributo.tipo === 'numero_con_guion') {
                return !isValorNumericoValido(atributo.tipo, valor)
            }
            if (atributo.tipo === 'date') {
                return !esFechaRealValida(valor)
            }
            return (valor || '').trim() === ''
        })

        const fechaInvalida = atributosElem.some((atributo, attrIndex) => (
            atributo.tipo === 'date' && !esFechaRealValida(valoresNormalizados[attrIndex])
        ))

        if (fechaInvalida) {
            return {
                valido: false,
                mensaje: 'La fecha ingresada no es válida.',
                erroresCampos,
            }
        }

        const fechaFueraDePeriodo = atributosElem.some((atributo, attrIndex) => (
            atributo.tipo === 'date' && !estaFechaDentroDelPeriodo(valoresNormalizados[attrIndex])
        ))

        if (fechaFueraDePeriodo) {
            return {
                valido: false,
                mensaje: 'La fecha debe estar dentro del período de evaluación.',
                erroresCampos,
            }
        }

        if (erroresCampos.includes(true)) {
            return {
                valido: false,
                mensaje: 'Todos los campos deben estar completos',
                erroresCampos,
            }
        }

        const indiceFechaInicio = atributosElem.findIndex((atributo) => esAtributoFechaConNombre(atributo, 'fecha inicio'))
        const indiceFechaFin = atributosElem.findIndex((atributo) => esAtributoFechaConNombre(atributo, 'fecha fin'))

        if (indiceFechaInicio !== -1 && indiceFechaFin !== -1) {
            const fechaInicio = normalizarFechaInput(valoresNormalizados[indiceFechaInicio])
            const fechaFin = normalizarFechaInput(valoresNormalizados[indiceFechaFin])

            if (fechaInicio && fechaFin && fechaInicio > fechaFin) {
                const erroresConFechas = [...erroresCampos]
                erroresConFechas[indiceFechaInicio] = true
                erroresConFechas[indiceFechaFin] = true
                return {
                    valido: false,
                    mensaje: 'La fecha de inicio no puede ser posterior a la fecha fin.',
                    erroresCampos: erroresConFechas,
                }
            }
        }

        return { valido: true, erroresCampos }
    }

    const agregar = () => {
        const valoresNormalizados = atributosElem.map((atributo, attrIndex) => {
            if (atributo.tipo === 'listado') {
                return getListadoSeleccionado(attrIndex, atributo.opciones)
            }

            if (atributo.tipo === 'numero_entero' || atributo.tipo === 'numero_decimal') {
                return sanitizeNumericInput(atributo.tipo, inputValues[attrIndex] || '')
            }

            if (atributo.tipo === 'numero_con_guion') {
                return sanitizeNumericInput(atributo.tipo, inputValues[attrIndex] || '')
            }

            if (atributo.tipo === 'date') {
                return normalizarFechaInput(inputValues[attrIndex] || '')
            }

            return (inputValues[attrIndex] || '').trim()
        })

        const { valido, mensaje, erroresCampos } = validarValores(valoresNormalizados)

        setInputValues(valoresNormalizados)

        const nuevosSearchValues = [...listadoSearchValues]
        atributosElem.forEach((atributo, index) => {
            if (atributo.tipo === 'listado' && valoresNormalizados[index]) {
                nuevosSearchValues[index] = valoresNormalizados[index]
            }
        })
        setListadoSearchValues(nuevosSearchValues)

        setInputErrors(erroresCampos)
        if (!valido) {
            Swal.fire({
                title: "Aviso",
                text: mensaje,
                icon: "warning",
                confirmButtonText: "Aceptar",
            })
            return
        }
        Swal.fire({
            title: "¿Estás seguro?",
            text: esEdicion ? "Esta acción actualizará el elemento." : "Esta acción guardará el elemento.",
            icon: "question",
            showCancelButton: true,
            background: darkMode ? "#1e1e1e" : "#fff",
            color: darkMode ? "#fff" : "#545454",
            confirmButtonColor: darkMode ? "#66BB6A" : "#4CAF50",
            cancelButtonColor: darkMode ? "#e57373" : "#d9534f",
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar",
        }).then(async (result) => {
            if (result.isConfirmed) {
                const res = []
                atributosElem.forEach((atributo, index) => {
                    let valor = valoresNormalizados[index];

                    if (atributo.tipo === "date" && valor) {
                        valor = convertToDisplayDate(valor)
                    }

                    res.push({
                        "id": atributo.id,
                        "nombre": atributo.nombre,
                        "valor": valor
                    })
                })

                const resultado = esEdicion
                    ? await globalStateActualizarInstancia(idActualizar, elem.indice_completo, res)
                    : await globalStateAgregarInstancia(elem.id_item_periodo, elem.indice_completo, res)

                if (resultado?.success) {
                    setInputValues(Array(atributosElem.length).fill(""))
                    setListadoSearchValues(Array(atributosElem.length).fill(""))
                    setListadoAbiertoIndex(null)
                    setListadoHighlightedIndices(Array(atributosElem.length).fill(-1))
                    closeModal()
                }
            }
        })
    }

    const cancelar = () => {
        setInputValues(Array(atributosElem.length).fill(""))
        setListadoSearchValues(Array(atributosElem.length).fill(""))
        setInputErrors(new Array(atributosElem.length).fill(false))
        setListadoAbiertoIndex(null)
        setListadoHighlightedIndices(Array(atributosElem.length).fill(-1))
        closeModal()
    }

    const cerrarSoloNotas = () => {
        closeModal()
    }

    const notasParaMostrar = [notas, elem?.notas, elem?.item?.notas]
        .find((value) => typeof value === "string" && value.trim() !== "")
        ?.trim() || ""

    const notasContainerStyle = {
        marginTop: 12,
        borderRadius: 8,
        border: darkMode ? '1px solid #495057' : '1px solid #dfe3e6',
        background: darkMode ? 'linear-gradient(180deg, #2a2f35 0%, #24282d 100%)' : 'linear-gradient(180deg, #fafbfc 0%, #f5f7f9 100%)',
        boxShadow: darkMode ? '0 2px 8px rgba(0, 0, 0, 0.22)' : '0 2px 8px rgba(72, 84, 96, 0.10)',
        overflow: 'hidden'
    }

    const notasHeaderStyle = {
        padding: '6px 10px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        color: darkMode ? '#e9ecef' : '#485460',
        backgroundColor: darkMode ? '#343a40' : '#eef1f4',
        borderBottom: darkMode ? '1px solid #495057' : '1px solid #d8dee4'
    }

    const notasBodyStyle = {
        margin: 0,
        padding: '10px 12px 12px 12px',
        whiteSpace: 'pre-line',
        lineHeight: 1.45,
        fontSize: 13,
        color: darkMode ? '#f8f9fa' : '#2f3a45',
        borderLeft: darkMode ? '4px solid #7d1b29' : '4px solid #a23c41',
        maxHeight: 130,
        overflowY: 'auto'
    }

    const modalContentStyles = {
        ...dynamicStyles.modalContent,
        width: 'min(1040px, 94vw)',
        maxHeight: '90vh',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        boxSizing: 'border-box'
    }

    const modalTitleStyles = {
        textAlign: 'center',
        margin: 0,
        fontSize: 23,
        lineHeight: 1.22,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere'
    }

    const modalBodyStyles = {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'visible',
        paddingRight: 2,
        marginTop: 8
    }

    const modalActionsStyles = {
        display: 'flex',
        justifyContent: soloNotas ? 'flex-end' : 'space-between',
        marginTop: 8,
        gap: 8,
        flexShrink: 0
    }

    const inputRowStyles = {
        ...dynamicStyles.inputContainer,
        display: 'grid',
        gridTemplateColumns: 'minmax(170px, 32%) minmax(0, 1fr)',
        alignItems: 'center',
        columnGap: 8,
        marginBottom: 8,
        padding: 8,
        fontSize: 16
    }

    const inputLabelStyles = {
        ...dynamicStyles.label,
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        margin: 0,
        fontSize: 14,
        lineHeight: 1.25,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere'
    }

    const inputFieldBaseStyles = {
        ...dynamicStyles.input,
        alignSelf: 'center',
        width: '100%',
        minWidth: 0,
        fontSize: 14,
        padding: 7,
        boxSizing: 'border-box'
    }

    const listadoContainerStyle = {
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
    }

    const listadoDropdownStyle = {
        marginTop: 6,
        maxHeight: 220,
        overflowY: 'auto',
        borderRadius: 10,
        border: darkMode ? '1px solid #495057' : '1px solid #d8dee4',
        backgroundColor: darkMode ? '#1f242a' : '#ffffff',
        boxShadow: darkMode ? '0 10px 24px rgba(0, 0, 0, 0.34)' : '0 12px 24px rgba(30, 41, 59, 0.14)'
    }

    const listadoOpcionStyle = (seleccionada, highlighted) => ({
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: darkMode ? '1px solid #2e353d' : '1px solid #eef1f4',
        backgroundColor: highlighted
            ? (darkMode ? 'rgba(151, 32, 52, 0.28)' : 'rgba(151, 32, 52, 0.14)')
            : seleccionada
            ? (darkMode ? 'rgba(151, 32, 52, 0.22)' : 'rgba(151, 32, 52, 0.08)')
            : 'transparent',
        color: darkMode ? '#f8f9fa' : '#1f2933',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: 1.35,
        fontSize: 13
    })

    const actualizarErrorCampo = (fieldIndex, value) => {
        const newErrors = [...inputErrors]
        newErrors[fieldIndex] = value.trim() === ""
        setInputErrors(newErrors)
    }

    const seleccionarOpcionListado = (fieldIndex, option) => {
        const newValues = [...inputValues]
        newValues[fieldIndex] = option
        setInputValues(newValues)

        const newSearchValues = [...listadoSearchValues]
        newSearchValues[fieldIndex] = option
        setListadoSearchValues(newSearchValues)

        actualizarErrorCampo(fieldIndex, option)
        setListadoAbiertoIndex(null)
        setListadoHighlightedIndices((prevState) => {
            const nextState = [...prevState]
            nextState[fieldIndex] = -1
            return nextState
        })
    }

    const getListadoOpcionesFiltradas = (fieldIndex, opciones) => (
        (opciones || []).filter((opcion) => opcion.toLowerCase().includes((listadoSearchValues[fieldIndex] || '').trim().toLowerCase()))
    )

    const handleListadoSearchChange = (fieldIndex, opciones, value) => {
        const newSearchValues = [...listadoSearchValues]
        newSearchValues[fieldIndex] = value
        setListadoSearchValues(newSearchValues)

        const matchExacto = (opciones || []).find((opcion) => opcion.toLowerCase() === value.trim().toLowerCase())
        const newValues = [...inputValues]
        newValues[fieldIndex] = matchExacto || ''
        setInputValues(newValues)

        actualizarErrorCampo(fieldIndex, matchExacto || '')
        setListadoAbiertoIndex(fieldIndex)

        const opcionesFiltradas = (opciones || []).filter((opcion) => opcion.toLowerCase().includes(value.trim().toLowerCase()))
        setListadoHighlightedIndices((prevState) => {
            const nextState = [...prevState]
            nextState[fieldIndex] = opcionesFiltradas.length > 0 ? 0 : -1
            return nextState
        })
    }

    const abrirListado = (fieldIndex, opciones) => {
        setListadoAbiertoIndex(fieldIndex)
        const opcionesFiltradas = getListadoOpcionesFiltradas(fieldIndex, opciones)
        setListadoHighlightedIndices((prevState) => {
            const nextState = [...prevState]
            const selectedIndex = opcionesFiltradas.findIndex((opcion) => opcion === inputValues[fieldIndex])
            nextState[fieldIndex] = selectedIndex >= 0 ? selectedIndex : (opcionesFiltradas.length > 0 ? 0 : -1)
            return nextState
        })
    }

    const cerrarListado = (fieldIndex) => {
        setListadoAbiertoIndex((prevState) => prevState === fieldIndex ? null : prevState)
        setListadoHighlightedIndices((prevState) => {
            const nextState = [...prevState]
            nextState[fieldIndex] = -1
            return nextState
        })
    }

    const handleListadoKeyDown = (event, fieldIndex, opciones) => {
        const opcionesFiltradas = getListadoOpcionesFiltradas(fieldIndex, opciones)

        if (event.key === 'Escape') {
            event.preventDefault()
            cerrarListado(fieldIndex)
            return
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            if (listadoAbiertoIndex !== fieldIndex) {
                abrirListado(fieldIndex, opciones)
                return
            }
            setListadoHighlightedIndices((prevState) => {
                const nextState = [...prevState]
                const currentIndex = nextState[fieldIndex]
                nextState[fieldIndex] = opcionesFiltradas.length === 0
                    ? -1
                    : Math.min(currentIndex + 1, opcionesFiltradas.length - 1)
                return nextState
            })
            return
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            if (listadoAbiertoIndex !== fieldIndex) {
                abrirListado(fieldIndex, opciones)
                return
            }
            setListadoHighlightedIndices((prevState) => {
                const nextState = [...prevState]
                const currentIndex = nextState[fieldIndex]
                nextState[fieldIndex] = opcionesFiltradas.length === 0
                    ? -1
                    : Math.max(currentIndex - 1, 0)
                return nextState
            })
            return
        }

        if (event.key === 'Enter' && listadoAbiertoIndex === fieldIndex) {
            const highlightedOption = opcionesFiltradas[listadoHighlightedIndices[fieldIndex]]
            if (highlightedOption) {
                event.preventDefault()
                seleccionarOpcionListado(fieldIndex, highlightedOption)
            }
        }
    }

    return (
        <div style={dynamicStyles.modalOverlay}>
            <div style={modalContentStyles}>
                {esEdicion ?
                    <h2 style={modalTitleStyles}>{elem.indice_completo} {elem.nombre}</h2>
                    :
                    <h2 style={modalTitleStyles}>{elem.indice_completo} {elem.item.nombre}</h2>
                }
                <div style={{ ...dynamicStyles.separator, marginBottom: 6 }} />
                <div style={modalBodyStyles}>
                    {!soloNotas && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {atributosElem.map((attr, index) => (
                                <div key={attr.id} style={inputRowStyles}>
                                    <label style={inputLabelStyles}>{attr.nombre}:</label>
                                    {attr.tipo === "listado" ? (
                                        <div style={listadoContainerStyle}>
                                            <input
                                                type="text"
                                                style={{
                                                    ...inputFieldBaseStyles,
                                                    paddingRight: 36
                                                }}
                                                placeholder="Seleccione una opción..."
                                                value={listadoSearchValues[index] || ''}
                                                onFocus={() => abrirListado(index, attr.opciones)}
                                                onChange={(e) => handleListadoSearchChange(index, attr.opciones, e.target.value)}
                                                onKeyDown={(e) => handleListadoKeyDown(e, index, attr.opciones)}
                                                onBlur={() => {
                                                    window.setTimeout(() => {
                                                        cerrarListado(index)
                                                    }, 120)
                                                }}
                                            />
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    right: 12,
                                                    top: 10,
                                                    fontSize: 12,
                                                    pointerEvents: 'none',
                                                    color: darkMode ? '#f8f9fa' : '#333'
                                                }}
                                            >
                                                ▾
                                            </span>
                                            {listadoAbiertoIndex === index && (
                                                <div style={listadoDropdownStyle}>
                                                    {getListadoOpcionesFiltradas(index, attr.opciones)
                                                        .map((opcion) => (
                                                            <div
                                                                key={opcion}
                                                                style={listadoOpcionStyle(
                                                                    inputValues[index] === opcion,
                                                                    getListadoOpcionesFiltradas(index, attr.opciones)[listadoHighlightedIndices[index]] === opcion
                                                                )}
                                                                onMouseDown={(e) => {
                                                                    e.preventDefault()
                                                                    seleccionarOpcionListado(index, opcion)
                                                                }}
                                                                title={opcion}
                                                            >
                                                                {opcion}
                                                            </div>
                                                        ))}
                                                    {getListadoOpcionesFiltradas(index, attr.opciones).length === 0 && (
                                                        <div style={{ ...listadoOpcionStyle(false, false), cursor: 'default', color: darkMode ? '#adb5bd' : '#6c757d' }}>
                                                            No se encontraron opciones.
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : attr.tipo === "texto_largo" ? (
                                        <textarea
                                            rows={4}
                                            style={{
                                                ...inputFieldBaseStyles,
                                                minHeight: 110,
                                                resize: 'vertical',
                                                lineHeight: 1.45,
                                                paddingTop: 10,
                                                paddingBottom: 10
                                            }}
                                            placeholder={`Ingrese ${attr.nombre.toLowerCase()}...`}
                                            value={inputValues[index]}
                                            onChange={(e) => {
                                                const newValues = [...inputValues]
                                                newValues[index] = e.target.value
                                                setInputValues(newValues)

                                                const newErrors = [...inputErrors];
                                                newErrors[index] = e.target.value.trim() === "";
                                                setInputErrors(newErrors);
                                            }}
                                        />
                                    ) : (
                                        <input
                                            type={getInputType(attr.tipo)}
                                            min={attr.tipo === 'date' && fechaMinimaEvaluacion ? fechaMinimaEvaluacion : undefined}
                                            max={attr.tipo === 'date' && fechaMaximaEvaluacion ? fechaMaximaEvaluacion : undefined}
                                            style={{
                                                ...inputFieldBaseStyles
                                            }}
                                            placeholder={`Ingrese ${attr.nombre.toLowerCase()}...`}
                                            value={inputValues[index]}
                                            onChange={(e) => {
                                                const newValues = [...inputValues]
                                                const valorSanitizado = sanitizeNumericInput(attr.tipo, e.target.value)
                                                newValues[index] = valorSanitizado
                                                setInputValues(newValues)

                                                const newErrors = [...inputErrors];
                                                if (attr.tipo === 'numero_entero' || attr.tipo === 'numero_decimal' || attr.tipo === 'numero_con_guion') {
                                                    newErrors[index] = !isValorNumericoValido(attr.tipo, valorSanitizado)
                                                } else {
                                                    newErrors[index] = e.target.value.trim() === "";
                                                }
                                                setInputErrors(newErrors);
                                            }}
                                            onKeyDown={(e) => {
                                                if (attr.tipo === 'numero_entero' || attr.tipo === 'numero_decimal') {
                                                    if (['e', 'E', '+', '-'].includes(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }

                                                if (attr.tipo === 'numero_con_guion') {
                                                    const teclasPermitidas = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End']
                                                    if (teclasPermitidas.includes(e.key)) return
                                                    if (e.key.length === 1 && !/[0-9-]/.test(e.key)) {
                                                        e.preventDefault()
                                                    }
                                                }
                                            }}
                                            inputMode={attr.tipo === 'numero_entero' || attr.tipo === 'numero_decimal' || attr.tipo === 'numero_con_guion' ? 'numeric' : undefined}
                                        />
                                    )}
                                </div>
                            ))}
                        </ul>
                    )}
                    {(notasParaMostrar || soloNotas) && (
                        <div style={notasContainerStyle}>
                            <div style={notasHeaderStyle}>Notas</div>
                            <p style={notasBodyStyle}>{notasParaMostrar || 'Sin notas disponibles para este item.'}</p>
                        </div>
                    )}
                </div>
                <div style={modalActionsStyles}>
                    {!soloNotas && (
                        <button onClick={() => agregar()}
                            style={{ ...dynamicStyles.modalSaveButton, marginTop: 0, padding: '7px 12px', fontSize: 16 }}
                            onMouseOver={(e) => e.target.style.backgroundColor = dynamicStyles.buttonHoverAgregar.backgroundColor}
                            onMouseOut={(e) => e.target.style.backgroundColor = dynamicStyles.modalSaveButton.backgroundColor}
                        >
                            Guardar
                        </button>
                    )}
                    <button style={{ ...dynamicStyles.modalCloseButton, marginTop: 0, padding: '7px 12px', fontSize: 16 }}
                        onClick={soloNotas ? cerrarSoloNotas : cancelar}
                        onMouseOver={(e) => e.target.style.backgroundColor = dynamicStyles.buttonHoverCancelar.backgroundColor}
                        onMouseOut={(e) => e.target.style.backgroundColor = dynamicStyles.modalCloseButton.backgroundColor}

                    >
                        {soloNotas ? 'Cerrar' : 'Cancelar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Modal
